import React from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { DialogOverlay, useOverlay, useContainerWidth, Divider, Paragraph, Button, Separator, Grid } from '@fs/zion-ui'
import { boolAttr } from '@fs/zion-frontend-friends'
import { css } from '@linaria/core'
import InfoCard from '../info-card/PersonInfoCard'
import Family from './components/Family'

const familyContainerCss = css`
  margin-top: -6px; /* This compensates for the top margin of the avatar for the first person in the family  */
`

const dialogContentCss = css`
  display: grid;
  grid-template-columns: calc(50% - 12px) calc(50% - 12px);
  max-width: 900px;
  grid-column-gap: 24px;

  &[full-width] {
    grid-template-columns: 100%;
  }
`

function OverlayFamily({ families, type, focusPersonId, customPersonLinkOrOverlay }) {
  const couples = type === 'parents' ? families.parents : families.spouses
  return (
    <Grid>
      {couples.map((family) => {
        const coupleKey = `${family.parent1?.id}-${family.parent2?.id}`
        return (
          <Family
            key={coupleKey}
            childrenLength={family.children?.length}
            family={family}
            type={type}
            event={family.event}
            focusPersonId={focusPersonId}
            hidePids
            showGenderText
            customPersonLinkOrOverlay={customPersonLinkOrOverlay}
          />
        )
      })}
    </Grid>
  )
}

function DialogContent({ spouseLength, families, focusPersonId, customPersonLinkOrOverlay, isMobile, parentLength }) {
  const fullWidth = isMobile || (spouseLength === 0 && parentLength > 0) || (spouseLength > 0 && parentLength === 0)
  const showDivider = parentLength > 0 && spouseLength > 0 && isMobile
  return (
    <div className={dialogContentCss} full-width={boolAttr(fullWidth)}>
      {spouseLength > 0 && (
        <OverlayFamily
          type="couple"
          families={families}
          focusPersonId={focusPersonId}
          customPersonLinkOrOverlay={customPersonLinkOrOverlay}
        />
      )}
      {showDivider && <Divider marginY="xxs" />}
      {parentLength > 0 && (
        <OverlayFamily
          type="parents"
          families={families}
          focusPersonId={focusPersonId}
          customPersonLinkOrOverlay={customPersonLinkOrOverlay}
        />
      )}
    </div>
  )
}

export default function FamilyCard({
  families,
  focusFamily,
  type,
  focusPersonId,
  customPersonLinkOrOverlay,
  ...props
}) {
  const [t] = useTranslation()
  const overlay = useOverlay()
  const atContainerWidth = useContainerWidth()
  const isMobile = atContainerWidth({ default: true, lg: false })

  const spouseLength = families.spouses?.length || 0
  const parentLength = families.parents?.length || 0
  const focusFamilyChildrenLength = focusFamily.children?.length || 0

  const showAdditionalFamilies =
    parentLength > 1 || spouseLength > 1 || focusFamilyChildrenLength > 5 || (parentLength === 1 && spouseLength === 1)

  const headerText = type === 'couple' ? t('info-cards.couple.header') : t('info-cards.parents.header')

  return (
    <InfoCard heading={headerText} {...props}>
      <div className={familyContainerCss}>
        <Family
          childrenLength={focusFamilyChildrenLength}
          family={{ ...focusFamily, children: focusFamily.children?.slice(0, 5) }}
          type={type}
          event={focusFamily.event}
          focusPersonId={focusPersonId}
          hidePids
          showGenderText
          hideParentsLabel
          customPersonLinkOrOverlay={customPersonLinkOrOverlay}
        />
        {showAdditionalFamilies && (
          <>
            {focusFamilyChildrenLength > 5 && (
              <>
                <Separator size="xxs" />
                <Paragraph size="xs">
                  {t('info-cards.additional-children.label', { count: focusFamilyChildrenLength - 5 })}
                </Paragraph>
              </>
            )}

            <DialogOverlay {...overlay} title={t('info-cards.family-members.header')}>
              <DialogContent
                spouseLength={spouseLength}
                families={families}
                focusPersonId={focusPersonId}
                customPersonLinkOrOverlay={customPersonLinkOrOverlay}
                isMobile={isMobile}
                parentLength={parentLength}
              />
            </DialogOverlay>

            <Separator size="nano" />

            <Button
              data-testid="tributes-view-all"
              linkName={`Family: ${type === 'couple' ? 'Spouse and Children' : 'Parents and Siblings'}: View All`}
              onClick={overlay.handleClick}
            >
              {t('info-cards.view-all.action')}
            </Button>
          </>
        )}
      </div>
    </InfoCard>
  )
}

// Stryker disable all
FamilyCard.propTypes = {
  /** Families Object */
  families: PropTypes.shape({
    /** Parents to display. */
    parents: PropTypes.arrayOf(
      PropTypes.shape({
        // Parent 1 person object.
        parent1: PropTypes.shape({}),
        // Parent 2 person object.
        parent2: PropTypes.shape({}),
        // Marriage event.
        event: PropTypes.shape({}),
        // Array of person objects.
        children: PropTypes.arrayOf(PropTypes.shape({})),
      })
    ).isRequired,

    /** Spouses to display. */
    spouses: PropTypes.arrayOf(
      PropTypes.shape({
        // Parent 1 person object.
        parent1: PropTypes.shape({}),
        // Parent 2 person object.
        parent2: PropTypes.shape({}),
        // Marriage event.
        event: PropTypes.shape({}),
        // Array of person objects.
        children: PropTypes.arrayOf(PropTypes.shape({})),
      })
    ).isRequired,
  }),

  focusFamily: PropTypes.shape({
    // Parent 1 person object.
    parent1: PropTypes.shape({}),
    // Parent 2 person object.
    parent2: PropTypes.shape({}),
    // Marriage event.
    event: PropTypes.shape({}),
    // Array of person objects.
    children: PropTypes.arrayOf(PropTypes.shape({})),
  }),

  type: PropTypes.string,

  /** If this PID is in the persons displayed, they will be higlighted. */
  focusPersonId: PropTypes.string,

  /**
   * If defined and person has customPersonLink, the person's name is linked to the customPersonLink.
   * If defined and person does not have a customPersonLink, this function is called to open the custom overlay.
   */
  customPersonLinkOrOverlay: PropTypes.func,
}
